import BackButton from '@components/backButton';
import PortfolioSelection from '@components/portfolioSelection';
import { Container, Grid, Typography } from '@mui/material';
import strings from '@src/app/strings/LocalisedStrings';
import Colors from '@src/app/config/colorConfig';
import { holdingsUrl } from '@src/app/utils/UrlBuilder';
import { FC, useEffect, useMemo, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import HoldingSecurityDetails from './HoldingSecurityDetails';
import TransactionsDataGrid from '../../transactions/TransactionsDataGrid';
import { useWindow } from '@src/app/hooks/useWindow';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { selectHoldings } from '@src/app/store/reducers/holdings/selectors';
import { getHoldingsAsync } from '@src/app/store/reducers/holdings/thunks';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { DateTime } from 'luxon';
import { ScreenState } from '@src/app/enums';
import { CommonLoader } from '@components/index';
import { getTransactionsAsync } from '@src/app/store/reducers/transactions/thunks';
import { Holding } from 'clientportalshared';
import { selectTransactions } from '@src/app/store/reducers/transactions/selectors';
import { setSecurityAsOfDate } from '@src/app/store/reducers/holdings';
import useQuery from '@src/app/hooks/useQuery';
import urlParamKeys from '@src/app/constants/urlParamKeys';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { LocalizationProvider } from '@mui/lab';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';

const HoldingsSecurity: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { portfoliosSelected, portfolioInfoState, earliestInception } = useAppSelector(selectPortfolios);
  const { state: transactionState, data: transactionData } = useAppSelector(selectTransactions);
  const { data, state: holdingState, securityAsOfDate } = useAppSelector(selectHoldings);
  const query = useQuery();
  const dateAsOf = query.get(urlParamKeys.dateAsOf);

  const [currentPageSize, setCurrentPageSize] = useState<number>(25);

  const { securityCode } = useParams<{ securityCode: string }>();
  const { resizing } = useWindow();

  const setAsAtDate = (date: DateRange) => {
    dispatch(setSecurityAsOfDate(date));
  };

  const fetchHoldings = async () => {
    await dispatch(
      getHoldingsAsync({ asOfDate: dateAsOf ?? DateTime.now().toISODate(), clientPortfolios: portfoliosSelected, securityCode: securityCode })
    );
  };

  const fetchTransactions = async () => {
    await dispatch(
      getTransactionsAsync({
        securityCode: securityCode,
        fromDate: securityAsOfDate.dateFrom,
        toDate: securityAsOfDate.dateTo,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  useEffect(() => {
    dispatch(
      setSecurityAsOfDate({
        dateFrom: earliestInception,
        dateTo: dateAsOf ?? DateTime.now().toISODate(),
      })
    );
  }, []);

  useEffect(() => {
    fetchHoldings();
  }, [portfoliosSelected]);

  useEffect(() => {
    if (securityAsOfDate.dateFrom.length === 0 || securityAsOfDate.dateTo.length === 0) {
      return;
    }
    fetchTransactions();
  }, [portfoliosSelected, securityAsOfDate]);

  const holdingSecurity: Holding | undefined = useMemo(() => {
    return data?.holdingList?.data?.[0];
  }, [data]);

  if (!dateAsOf || !securityCode || securityCode.length < 1) {
    return <Redirect to={holdingsUrl()} />;
  }
  if (portfolioInfoState === ScreenState.Loading || holdingState === ScreenState.Loading) {
    return <CommonLoader />;
  }
  const transactions = transactionData?.transactions ?? [];
  const transactionsLoading = transactionState === ScreenState.Loading;
  return (
    <Container>
      <Grid container direction="column" spacing={2} mb={6}>
        <Grid item container alignItems="center" justifyContent="space-between">
          <Grid item>
            <BackButton />
          </Grid>
          <Grid item>
            <PortfolioSelection allowCombining={false} />
          </Grid>
        </Grid>
        <HoldingSecurityDetails
          holdingsDate={dateAsOf}
          price={holdingSecurity?.price ?? 0}
          units={holdingSecurity?.units ?? 0}
          priceDate={holdingSecurity?.priceDate ?? DateTime.now().toISODate()}
          value={holdingSecurity?.value ?? 0}
          costBase={holdingSecurity?.costBase ?? 0}
          estimatedGainOrLoss={holdingSecurity?.estimatedGainOrLoss ?? 0}
          percentageOfPortfolio={holdingSecurity?.percentageOfPortfolio ?? 0}
          totalReturnValue={holdingSecurity?.totalReturnValue ?? 0}
          securityCode={securityCode}
          securityName={holdingSecurity?.securityName ?? 'Security name not found'}
        />
        <Grid item>
          <Typography fontWeight={700} fontSize={20} color={Colors.gray800}>
            {strings.allTransactions}
          </Typography>
        </Grid>
        <Grid item>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangePicker
              inceptionDate={earliestInception}
              dateRange={securityAsOfDate}
              setDateRange={(dateRange: DateRange) => setAsAtDate(dateRange)}
            />
          </LocalizationProvider>
        </Grid>
        {!resizing && (
          <Grid item>
            <TransactionsDataGrid
              currentPageSize={currentPageSize}
              setCurrentPageSize={setCurrentPageSize}
              hideSecurity
              isLoading={transactionsLoading}
              data={transactions}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default HoldingsSecurity;
